import { CurrencyPipe, DatePipe, DecimalPipe } from '@angular/common'
import { HttpBackend, HttpClient, HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http'
import { APP_INITIALIZER, ApplicationConfig, importProvidersFrom } from '@angular/core'
import { MAT_BUTTON_TOGGLE_DEFAULT_OPTIONS } from '@angular/material/button-toggle'
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS } from '@angular/material-moment-adapter'
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core'
import { MatDialogModule } from '@angular/material/dialog'
import { MatSnackBarModule } from '@angular/material/snack-bar'
import { provideAnimations } from '@angular/platform-browser/animations'
import { provideRouter, withInMemoryScrolling, withPreloading } from '@angular/router'
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core'
import { TranslateHttpLoader } from '@ngx-translate/http-loader'
import { NotificationsService, OPTIONS, optionsFactory } from 'angular2-notifications'
import { KeycloakBearerInterceptor, KeycloakService } from 'keycloak-angular'
import { InlineSVGModule } from 'ng-inline-svg-2'
import { QuillModule } from 'ngx-quill'
import Aura from '@primeng/themes/aura'
import { providePrimeNG } from 'primeng/config'
import { DialogService } from 'primeng/dynamicdialog'
import { firstValueFrom } from 'rxjs'
import { routes } from './app.routes'
import { AppService } from './app.service'
import { AppIntegerTwoDecimalPipe } from './shared/pipes/app-integer-two-decimal/app-integer-two-decimal.pipe'
import { AppBasicService } from './shared/services/app-basic.service'
import { AppCodeService } from './shared/services/app-code.service'
import { AppConfigService } from './shared/services/app-config.service'
import { AppCoreService } from './shared/services/app-core.service'
import { AppCustomDateAdapter, CUSTOM_DATE_FORMATS } from './shared/services/app-custom-date-adapter.service'
import { AppNotificationService } from './shared/services/app-notification.service'
import { AppSpinnerService } from './shared/services/app-spinner.service'
import { AppRoutePreloadingStrategy } from './shared/services/app-route-preloading.strategy'
import { AppThemeService } from './shared/services/app-theme.service'
import { AppAuthService } from './shared/services/app-auth.service'
import { storageProviders } from './shared/store/storage'

interface KeycloakConfig {
   keycloak: {
      authUrl: string
      realm: string
      clientId: string
   }
}

function initializeTranslation(translate: TranslateService) {
   return () => {
      translate.setDefaultLang('en-US')
      const browserLang = navigator.language
      const langToUse = browserLang === 'en-US' ? browserLang : 'en-US'
      return firstValueFrom(translate.use(langToUse))
   }
}

function initializeKeycloak(keycloak: KeycloakService, httpClient: HttpClient) {
   return async () => {
      const kc = await firstValueFrom(httpClient.get<KeycloakConfig>(`assets/configs/properties.json?ts=${Date.now()}`))
      return keycloak.init({
         config: {
            url: kc.keycloak.authUrl,
            realm: kc.keycloak.realm,
            clientId: kc.keycloak.clientId
         },
         initOptions: {
            enableLogging: true,
            checkLoginIframe: false,
            flow: 'standard',
            onLoad: 'check-sso',
            responseMode: 'query'
         },
         enableBearerInterceptor: true,
         bearerPrefix: 'Bearer '
      })
   }
}

function httpLoaderFactory(handler: HttpBackend) {
   return new TranslateHttpLoader(new HttpClient(handler), './assets/i18n/')
}

function initializeAppService(_appService: AppService) {
   return () => undefined
}

export const appConfig: ApplicationConfig = {
   providers: [
      importProvidersFrom(
         InlineSVGModule.forRoot(),
         MatDialogModule,
         MatSnackBarModule,
         QuillModule.forRoot(),
         TranslateModule.forRoot({
            defaultLanguage: 'en-US',
            useDefaultLang: true,
            loader: {
               provide: TranslateLoader,
               useFactory: httpLoaderFactory,
               deps: [HttpBackend]
            }
         })
      ),
      provideAnimations(),
      providePrimeNG({
         theme: {
            preset: Aura,
            options: {
               darkModeSelector: false,
               cssLayer: { name: 'primeng', order: 'primeng' }
            }
         }
      }),
      NotificationsService,
      {
         provide: OPTIONS,
         useValue: {
            timeOut: 5000,
            showProgressBar: true,
            pauseOnHover: true,
            clickToClose: true,
            position: ['bottom', 'right'],
            lastOnBottom: true,
            maxStack: 3,
            maxLength: 500,
            theClass: 'app-notify',
            preventDuplicates: true,
            icons: {
               success: '<mat-icon class="mat-icon material-icons">check_outline</mat-icon>',
               alert: '<mat-icon class="mat-icon material-icons">notification_important</mat-icon>',
               error: '<mat-icon class="mat-icon material-icons">error_outline</mat-icon>',
               info: '<mat-icon class="mat-icon material-icons">info</mat-icon>',
               warn: '<mat-icon class="mat-icon material-icons">warning</mat-icon>'
            }
         }
      },
      { provide: 'options', useFactory: optionsFactory, deps: [OPTIONS] },
      AppService,
      { provide: APP_INITIALIZER, multi: true, deps: [AppService], useFactory: initializeAppService },
      provideRouter(routes, withInMemoryScrolling({ scrollPositionRestoration: 'enabled' }), withPreloading(AppRoutePreloadingStrategy)),
      ...storageProviders,
      DecimalPipe,
      CurrencyPipe,
      AppIntegerTwoDecimalPipe,
      { provide: APP_INITIALIZER, multi: true, deps: [TranslateService], useFactory: initializeTranslation },
      {
         provide: APP_INITIALIZER,
         multi: true,
         deps: [AppConfigService],
         useFactory: (configService: AppConfigService) => () => configService.initRuntimeConfiguration()
      },
      { provide: APP_INITIALIZER, useFactory: initializeKeycloak, multi: true, deps: [KeycloakService, HttpClient] },
      { provide: HTTP_INTERCEPTORS, useClass: KeycloakBearerInterceptor, multi: true },
      KeycloakService,
      AppSpinnerService,
      AppNotificationService,
      AppAuthService,
      { provide: MAT_BUTTON_TOGGLE_DEFAULT_OPTIONS, useValue: { hideSingleSelectionIndicator: true } },
      { provide: MAT_DATE_LOCALE, useValue: 'en-US' },
      { provide: DateAdapter, useClass: AppCustomDateAdapter, deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS] },
      { provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMATS },
      { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } },
      AppCoreService,
      AppCodeService,
      AppBasicService,
      DatePipe,
      DialogService,
      AppThemeService,
      provideHttpClient(withInterceptorsFromDi())
   ]
}
